import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import RelatedTreatments from '../components/related-treatments'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '30-45 minutes',
  session: '1 session',
  results: 'Results up to 3-4 weeks',
  cost: 'From £49.99',
  downtime: 'None',
}

const relatedTreatments = [
  {
    name: 'Fat Dissolving Injections Cheshunt',
    slug: 'fat-dissolving',
    description:
      'Pair glowing skin with a more sculpted silhouette using non-surgical fat dissolving injections.',
  },
  {
    name: 'Dermal Fillers Cheshunt',
    slug: 'dermal-fillers-cheshunt',
    description:
      'Restore facial volume and soften deeper lines to complement your brighter, smoother complexion.',
  },
  {
    name: 'Lash Lift & Tint Cheshunt',
    slug: 'lash-lift-and-tint',
    description:
      'Complete your fresh-faced look with naturally curled, darkened lashes that last 6–8 weeks.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much does dermaplaning cost in Cheshunt?',
    answer:
      'Dermaplaning treatments at Ada Aesthetics in Cheshunt start from £49.99 for simple dermaplaning (30 minutes). For luxury dermaplaning with cleanse, steam, extractions, jelly mask, and facial massage, it is £69.99 (45 minutes). We also offer combination treatments like dermaplaning with hydrafacial and chemical peel for £99. Book a free consultation at our Cheshunt clinic to find the best option for your skin.',
  },
  {
    question:
      'What is the difference between dermaplaning and luxury dermaplaning?',
    answer:
      'Think of luxury dermplaning as a dermaplane facial. This safe and advanced exfoliation immediately removes dead skin and peach fuzz for brighter, smoother skin. But your treatment should not stop there - now is the perfect time for an anti-ageing facial. Skincare actives can penetrate far more deeply because they do not have to get past a thick layer of dead skin.',
  },
  {
    question: 'What happens during luxury dermaplaning?',
    answer:
      'Firstly, your Ada Aesthetics practitioner will deep cleanse your face, ' +
      'followed by an antiseptic toner to make it spotless. They will gently ' +
      'remove peach fuzz and dead skin cells from the top layer of skin with ' +
      'a scalpel, followed by a relaxing facial to soothe your skin. ' +
      'Enjoy a relaxing facial massage, then choose from a detoxifying,' +
      ' hydrating or firming mask for younger, healthier skin. ' +
      'No downtime – just glowing skin.',
  },
  {
    question: 'Is luxury dermaplaning safe?',
    answer:
      'At Ada Aesthetics, our practitioners are highly trained to safely shave ' +
      'away peach fuzz with a sterile, ultra-fine surgical scalpel.' +
      ' Their short, methodical strokes are so light that you will barely ' +
      'feel it – some even find it relaxing. It is completely pain-free' +
      ' because they only remove the very top skin cells that are already dead.',
  },
]

const Dermaplaning: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Dermaplaning Facial in Cheshunt',
    'Professional dermaplaning treatments in Cheshunt, Hertfordshire. Remove dead skin and peach fuzz for brighter, smoother skin. Simple and luxury options available.',
    '49.99'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Dermaplaning Cheshunt', url: 'https://adaaesthetics.uk/dermaplaning' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Dermaplaning Cheshunt | Luxury Facial Treatment | Ada Aesthetics"
        description="Professional dermaplaning in Cheshunt from £49.99. Remove peach fuzz & dead skin for glowing skin. Simple & luxury options. Book consultation: 07478705252"
        keywords="dermaplaning Cheshunt, facial exfoliation, peach fuzz removal, luxury dermaplaning Hertfordshire"
        url="https://adaaesthetics.uk/dermaplaning"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax titleName="Dermaplaning Facial" customClass="page_title" />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Reveal Instantly Smoother, Brighter Skin with Dermaplaning in Cheshunt</h1>
              <p className="excerpt">
                Does your make-up catch on fine facial hair or sit on dull, uneven skin? Dermaplaning at Ada Aesthetics
                in Cheshunt gently removes dead skin cells and peach fuzz in just 30–45 minutes, leaving your complexion
                smoother, brighter and perfectly prepped for skincare and makeup. From £49.99, with simple and luxury
                facial options available.
              </p>

              <h2>What is Dermaplaning?</h2>
              <p>
                Dermaplaning is a safe and advanced exfoliation treatment that immediately removes dead skin and peach fuzz for brighter, smoother skin. At Ada Aesthetics in Cheshunt, our practitioners use a sterile, ultra-fine surgical scalpel to gently shave away the very top layer of dead skin cells and vellus hair (peach fuzz).
              </p>
              <p>
                If you have a big night out coming up and need to improve the
                texture of your skin quickly, luxury dermaplaning could be the
                perfect answer. This treatment creates the perfect canvas for makeup application and allows skincare products to penetrate more deeply.
              </p>

              <h2>Benefits of Dermaplaning</h2>
              <ul>
                <li><strong>Removes dead skin cells</strong> for brighter, smoother complexion</li>
                <li><strong>Eliminates peach fuzz</strong> that traps makeup and oil</li>
                <li><strong>Enhances product absorption</strong> - skincare penetrates deeper</li>
                <li><strong>Improves makeup application</strong> - flawless, smooth finish</li>
                <li><strong>No downtime</strong> - immediate results</li>
                <li><strong>Pain-free treatment</strong> - gentle and relaxing</li>
                <li><strong>Results last 3-4 weeks</strong> - long-lasting smoothness</li>
              </ul>

              <h2>Simple vs Luxury Dermaplaning</h2>
              <p>
                At Ada Aesthetics in Cheshunt, we offer two dermaplaning options:
              </p>
              <p>
                <strong>Simple Dermaplaning (£49.99, 30 minutes):</strong> Quick exfoliation treatment that removes dead skin and peach fuzz for immediate smoothness and glow.
              </p>
              <p>
                <strong>Luxury Dermaplaning (£69.99, 45 minutes):</strong> Think of luxury dermaplaning as a dermaplane facial. This comprehensive treatment includes deep cleanse, steam, extractions, jelly mask, and facial massage. After removing dead skin and peach fuzz, now is the perfect time for an anti-ageing facial. Skincare actives can penetrate far more deeply because they do not have to get past a thick layer of dead skin.
              </p>

              <h2>What Happens During Luxury Dermaplaning?</h2>
              <p>
                Firstly, your Ada Aesthetics practitioner will deep cleanse your face,
                followed by an antiseptic toner to make it spotless. They will gently
                remove peach fuzz and dead skin cells from the top layer of skin with
                a scalpel, followed by a relaxing facial to soothe your skin.
                Enjoy a relaxing facial massage, then choose from a detoxifying,
                hydrating or firming mask for younger, healthier skin.
                No downtime – just glowing skin.
              </p>

              <h2>Is Dermaplaning Safe?</h2>
              <p>
                At Ada Aesthetics in Cheshunt, our practitioners are highly trained to safely shave
                away peach fuzz with a sterile, ultra-fine surgical scalpel.
                Their short, methodical strokes are so light that you will barely
                feel it – some even find it relaxing. It is completely pain-free
                because they only remove the very top skin cells that are already dead.
              </p>

              <h2>Why Choose Ada Aesthetics for Dermaplaning in Cheshunt?</h2>
              <ul>
                <li><strong>Highly trained practitioners</strong> with advanced dermaplaning expertise</li>
                <li><strong>Sterile, professional equipment</strong> - safety first</li>
                <li><strong>Two treatment options</strong> - simple and luxury</li>
                <li><strong>Immediate results</strong> - glowing skin in under an hour</li>
                <li><strong>Competitive pricing</strong> from £49.99</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
              </ul>

              <h2>Book Your Dermaplaning Treatment in Cheshunt</h2>
              <p>
                Ready for smoother, glowing skin? Join clients who trust us for professional dermaplaning treatments.
                Schedule your appointment today - our experienced team will help you choose between simple and luxury dermaplaning to achieve your perfect skin goals.
              </p>
              <p>
                <strong>Book today to secure your appointment</strong> - Call us at <strong>07478705252</strong> or reserve your treatment online.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Dermaplaning Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>
          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default Dermaplaning

export const pageQuery = graphql`
  query Dermaplaning {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
